import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "row starter-main mt-5 pt-5" }
const _hoisted_3 = { class: "d-flex justify-content-end align-items-center gap-3 mt-3 mb-4" }
const _hoisted_4 = { class: "d-flex align-items-center gap-2" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "pagination-mini" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = ["disabled"]

import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useCrud } from "@/composables/commen/useCrud"; // Adjust path
import BaseModal from "@/components/commen/modal/BaseModal.vue"; // Adjust path
import BaseForm from "@/components/commen/modal/BaseForm.vue"; // Adjust path
import apiService from "@/services/apiService"; 
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";
const API_RESOURCE = "/common/industry-users";
const pageTitle = "Industry Users";
const title = "Industry Users List";


export default /*@__PURE__*/_defineComponent({
  __name: 'industryuserList ',
  setup(__props) {

const { canCreate } = usePermissions();

const options = ref([]);

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// pagination local UI state (mirrors backend, but we keep per_page from UI)
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed (same advanced logic)
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

const rows = ref([]);
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Industry", field: "industry_title", sortable: true }, 
  ],
  data: rows, // keep as a ref
  actions: [true],
  loading: crud.isLoading,
});


watch(crud.data, (newVal) => {
  tableConfig.value.data = newVal;
});
watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination into tablePagination, but DO NOT overwrite UI per_page except if UI didn't set it.
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;

    // keep UI rows per page unless backend changed
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  // when user changes rows, reset to page 1 and fetch
  currentPage.value = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  // protect bounds
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};


// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({ title: "", industry_id: "" }); // Initial create data
const editingItemData = ref({ _id: "", title: "", industry_id: "" }); // Initial edit/view data

// --- Form Definitions ---
const createFormFields = computed(() => [
  {
    key: "industry_id",
    label: "Industry",
    type: "select",
    required: true,
    placeholder: "Select Industry",
    options: options.value, // ✅ stays reactive
  },
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
  },
]);
const editFormFields = computed(() => [
  {
    key: "industry_id",
    label: "Industry",
    type: "select",
    required: true,
    placeholder: "Select Industry",
    options: options.value, // ✅ stays reactive
  },
  {
    key: "title",
    label: "Title",
    type: "text",
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
]);

// --- Form Validation (Example) ---
const isCreateFormValid = computed(() => !newItemData.value.title.trim());
const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = {
    ...row,
    industry_id:
      typeof row.industry_id === "object"
        ? row.industry_id._id
        : row.industry_id,
  };
  isEditModalOpen.value = true; // Open modal
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row }; // Populate form
  isEditModalOpen.value = true; // Open modal
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this item?")) {
    await crud.deleteItem(id); // Use composable function 
     await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) {
    return;
  }

  // Access form data if needed directly from ref, or pass via emit payload
  // const formData = createFormRef.value?.formData;
  const result = await crud.createItem(newItemData.value); // Use composable
  if (result) { 
    closeCreateModal();
    newItemData.value = { title: "", color: "#cccccc" }; // Reset form data
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
     await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", color: "#cccccc" }; // Reset form data
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", color: "#cccccc" }; // Reset form data
};

// Lifecycle
onMounted(async () => {
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});

const fetchData = async () => {
  try {
    const resultArray = await apiService.get("common/industries");
    if (Array.isArray(resultArray)) {
      options.value = JSON.parse(JSON.stringify(resultArray));
    } else {
      options.value = [];
    }
  } catch (error: any) {
    options.value = [];
  }
};

watch(editingItemData, (val) => {
  console.log("Current selected industry_id:", val.industry_id);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(TableTitle, {
          title: title,
          pageTitle: pageTitle,
          icon: "fas fa-book",
          showAction: true,
          actionLabel: "Create New",
          modal: 'createNew',
          disabled: !_unref(canCreate)('data')
        }, null, 8, ["disabled"]),
        _createVNode(CommonTable2, {
          tableObj: tableConfig.value,
          title: title,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onView: handleView,
          permissionKey: "data"
        }, null, 8, ["tableObj"])
      ])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-muted" }, "Rows per page:", -1)),
        _withDirectives(_createElementVNode("select", {
          class: "form-select form-select-sm rows-select",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tablePagination.value.per_page) = $event)),
          onChange: changeRows
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList([5, 10, 20, 50, 100], (n) => {
            return _createElementVNode("option", {
              key: n,
              value: n
            }, _toDisplayString(n), 9, _hoisted_5)
          }), 64))
        ], 544), [
          [_vModelSelect, tablePagination.value.per_page]
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "page-btn prev_btn",
          disabled: !tablePagination.value.has_prev,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (handlePageChange(tablePagination.value.current_page - 1)))
        }, " « ", 8, _hoisted_7),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePages.value, (p, index) => {
          return (_openBlock(), _createElementBlock("button", {
            key: index,
            class: _normalizeClass(["page-btn", {
          'page-active': p === tablePagination.value.current_page,
          'disabled-dots': p === '...',
        }]),
            disabled: p === '...',
            onClick: ($event: any) => (p !== '...' && handlePageChange(p))
          }, _toDisplayString(p), 11, _hoisted_8))
        }), 128)),
        _createElementVNode("button", {
          class: "page-btn next_btn",
          disabled: !tablePagination.value.has_next,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (handlePageChange(tablePagination.value.current_page + 1)))
        }, " » ", 8, _hoisted_9)
      ])
    ]),
    _createVNode(BaseModal, {
      id: "createNew",
      title: `Create ${pageTitle}`,
      visible: isCreateModalOpen.value,
      onClose: closeCreateModal,
      onSave: handleSaveCreate,
      "save-button-disabled": isCreateFormValid.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: createFormFields.value,
          modelValue: newItemData.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newItemData).value = $event)),
          onSubmit: handleSaveCreate,
          ref: "createFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "save-button-disabled"]),
    _createVNode(BaseModal, {
      id: "editModal",
      title: `${isViewMode.value ? 'View' : 'Edit'} ${pageTitle}`,
      visible: isEditModalOpen.value,
      onClose: closeEditModal,
      onSave: handleSaveEdit,
      "show-save-button": !isViewMode.value,
      "save-button-disabled": isEditFormValid.value
    }, {
      body: _withCtx(() => [
        _createVNode(BaseForm, {
          fields: editFormFields.value,
          modelValue: editingItemData.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editingItemData).value = $event)),
          onSubmit: handleSaveEdit,
          ref: "editFormRef"
        }, null, 8, ["fields", "modelValue"])
      ]),
      _: 1
    }, 8, ["title", "visible", "show-save-button", "save-button-disabled"])
  ], 64))
}
}

})